import sentryFetch from '@amzn/sentry-fetch';
import UserInfoStore from 'src/app/store/UserInfoStore';
import {logger} from 'src/app/util/Logger';
import {recordAPICall, recordAPIError, recordAPISuccess} from 'src/app/util/Metrics';
import {REGION, STAGE} from 'src/app/config/EnvConfig';
import {convertAwsRegion, EEP_API_URL_MAP} from 'src/app/config/ApiConfig';

/**
 * Returns the API base URL for the current stage and region.
 * Uses the centralized EEP_API_URL_MAP from ApiConfig.
 */
const getApiEndpoint = (): string => {
    const stage = (STAGE as string).toLowerCase();
    const region = convertAwsRegion(REGION as string);
    return EEP_API_URL_MAP[stage]?.[region]?.endpoint ?? '';
};

const API_BASE = getApiEndpoint();

/**
 * Core fetch wrapper using sentryFetch for ALI Midway CORS authentication.
 * sentryFetch with followMidwayStepUp:true handles the full Midway login redirect
 * automatically when no valid session exists — no custom redirect logic needed.
 */
export const apiFetch = async (method: string, path: string, body?: any): Promise<any> => {
    const url = `${API_BASE}${path}`;
    const response = await sentryFetch(url, {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
        sentryOptions: {followMidwayStepUp: true}
    } as any);
    if (!response.ok) {
        throw new Error(`API ${method} ${path} failed: ${response.status}`);
    }
    return response.json();
};

/**
 * Logs API request information
 */
export const logRequest = (operation: string, request: any) => {
    try {
        logger.info(`[${operation}] Request initiated`, {
            operation,
            userId: UserInfoStore.value,
            requestData: request,
            timestamp: new Date().toISOString()
        });
        recordAPICall(operation);
    } catch (error) {
        console.error('Logging error:', error);
    }
};

/**
 * Logs API response information
 */
export const logResponse = (operation: string, duration: number) => {
    try {
        logger.info(`[${operation}] Response received`, {
            operation,
            userId: UserInfoStore.value,
            duration,
            timestamp: new Date().toISOString()
        });
        recordAPISuccess(operation, duration);
    } catch (error) {
        console.error('Logging error:', error);
    }
};

/**
 * Logs API errors with full context
 */
export const logError = (operation: string, error: any, request?: any) => {
    try {
        logger.error(`[${operation}] Error occurred`, {
            operation,
            error: error.message || error,
            errorStack: error.stack,
            userId: UserInfoStore.value,
            requestData: request,
            timestamp: new Date().toISOString()
        });
        recordAPIError(operation);
    } catch (error) {
        console.error('Logging error:', error);
    }
};
